import type { Table } from '@tanstack/react-table'
import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

interface HorizontalScrollWrapperProps<TData> {
  'data-e2e'?: string
  bottomOffset: number
  children: React.ReactNode
  horizontalScrollingArea?: HTMLDivElement | null
  setHorizontalScrollingAreaRef?: (element: HTMLDivElement | null) => void
  table: Table<TData>
}

const ContentWrapper = styled.div<{ $hasScrollbar: boolean }>`
  width: 100%;
  margin-left: ${props => props.theme.spacings.medium};
  margin-right: ${props => props.theme.spacings.medium};
  max-width: calc(
    100% - ${props => props.theme.spacings.medium} * ${props => (props.$hasScrollbar ? 1 : 2)}
  );
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`

const Scrollbar = styled.div<{ $bottom: number; $left: number; $width: number }>`
  position: fixed;
  bottom: ${props => props.$bottom}px;
  left: ${props => props.$left}px;
  width: ${props => props.$width}px;
  height: 17px;
  overflow-x: auto;
  overflow-y: hidden;
  z-index: 1;
`

const SHOW_THRESHOLD = 20
const HIDE_THRESHOLD = 0

export const HorizontalScrollWrapper = <TData,>({
  'data-e2e': dataE2e,
  bottomOffset,
  children,
  horizontalScrollingArea,
  setHorizontalScrollingAreaRef,
  table,
}: HorizontalScrollWrapperProps<TData>) => {
  const scrollbarRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState({ left: 0, width: 0 })
  const [hasScrollbar, setHasScrollbar] = useState(false)

  const tableWidth = table.getTotalSize()

  useEffect(() => {
    if (!horizontalScrollingArea) return undefined

    const update = () => {
      const { left, width } = horizontalScrollingArea.getBoundingClientRect()
      const delta = tableWidth - horizontalScrollingArea.clientWidth

      setPos(prev => (prev.left !== left || prev.width !== width ? { left, width } : prev))
      setHasScrollbar(prev => {
        if (!prev && delta > SHOW_THRESHOLD) return true
        if (prev && delta <= HIDE_THRESHOLD) return false
        return prev
      })
    }

    const resizeObserver = new ResizeObserver(update)

    update()
    resizeObserver.observe(horizontalScrollingArea)

    return () => resizeObserver.disconnect()
  }, [tableWidth, horizontalScrollingArea])

  useEffect(() => {
    const scrollbar = scrollbarRef.current
    const scrollTarget = horizontalScrollingArea

    if (!scrollbar || !scrollTarget || !hasScrollbar) return undefined

    const handleScroll = () => {
      scrollTarget.scrollLeft = scrollbar.scrollLeft
    }

    scrollbar.addEventListener('scroll', handleScroll, { passive: true })

    return () => scrollbar.removeEventListener('scroll', handleScroll)
  }, [hasScrollbar, horizontalScrollingArea])

  return (
    <>
      <ContentWrapper
        data-e2e={`${dataE2e}--content`}
        ref={setHorizontalScrollingAreaRef}
        $hasScrollbar={hasScrollbar}
      >
        {children}
      </ContentWrapper>

      {hasScrollbar && (
        <Scrollbar
          data-e2e={`${dataE2e}--scrollbar`}
          $bottom={bottomOffset}
          $left={pos.left}
          $width={pos.width}
          ref={scrollbarRef}
        >
          <div style={{ width: tableWidth, height: '1px' }} />
        </Scrollbar>
      )}
    </>
  )
}
